import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Button, Segmented, Spin, message } from 'antd';
import DayColumn from '../../components/tracker/Plan/DayColumn';
import BacklogPanel from '../../components/tracker/Plan/BacklogPanel';
import PriorityGrid from '../../components/tracker/Plan/PriorityGrid';
import GroupedTaskList from '../../components/tracker/Plan/GroupedTaskList';
import TaskEditModal from '../../components/tracker/Plan/TaskEditModal';
import ManualEntryModal from '../../components/tracker/Timer/ManualEntryModal';
import TimeEntryList from '../../components/tracker/Timer/TimeEntryList';
import formatDuration from '../../utils/formatDuration';
import api from '../../lib/api';
import type { PlanGroupBy, PlanPageProps, PlanResult, PlanTask, Priority } from '../../types/plan';
import type { TimeEntry } from '../../types/timeEntry';

const SLICES: { value: PlanGroupBy; label: string }[] = [
  { value: 'date', label: '时间跨度' },
  { value: 'priority', label: '优先级' },
  { value: 'project', label: '项目' },
  { value: 'category', label: '生活面向' },
  { value: 'work_type', label: '精力类型' },
];

const Plan: React.FC<PlanPageProps> = ({ plan: initialPlan, projects, categories, week_summary }) => {
  const [plan, setPlan] = useState<PlanResult>(initialPlan);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<PlanTask | null>(null);

  const reload = useCallback(
    async (groupBy: PlanGroupBy = plan.group_by) => {
      setLoading(true);
      try {
        const fresh = await api.plan.fetch({ groupBy });
        setPlan(fresh);
      } catch (error) {
        message.error(error instanceof Error ? error.message : '加载失败');
      } finally {
        setLoading(false);
      }
    },
    [plan.group_by]
  );

  const patchTask = useCallback(
    async (taskId: number, patch: Record<string, unknown>) => {
      try {
        await api.plan.updateTask(taskId, patch);
        await reload();
      } catch (error) {
        message.error(error instanceof Error ? error.message : '更新失败');
      }
    },
    [reload]
  );

  const handleDropToDate = useCallback(
    (taskId: number, date: string | null) => patchTask(taskId, { planned_for: date }),
    [patchTask]
  );

  const handleDropToPriority = useCallback(
    (taskId: number, patch: { priority: Priority }) => patchTask(taskId, patch),
    [patchTask]
  );

  // 四象限/项目/面向/精力类型视图里没有可拖拽的"今天/明天"落点（见 Task 6/8
  // 的决策说明），用这两个按钮代替。`plan.from` / `plan.to` 不管当前
  // group_by 是什么都固定是今天/明天——`Plan.tsx` 从没把 `from`/`to` 传给
  // `api.plan.fetch`（只传了 `groupBy`），`PlanQuery` 的默认值就是
  // `Date.current` / `Date.tomorrow`，所以直接复用这两个字段，不需要另外
  // 在前端算日期、也不需要后端多吐两个字段。
  const handleQuickSchedule = useCallback(
    (task: PlanTask, when: 'today' | 'tomorrow') =>
      patchTask(task.id, { planned_for: when === 'today' ? plan.from : plan.to }),
    [patchTask, plan.from, plan.to]
  );

  const handleToggle = useCallback(
    (task: PlanTask) => patchTask(task.id, { completed: !task.completed }),
    [patchTask]
  );

  // 预估合计。设计稿顶部还有「本周已记」「深度工作」两个实花数字，
  // 那依赖 TimeEntry，属阶段 3，本阶段只显示预估。
  const plannedMinutes = useMemo(
    () => plan.groups.reduce((sum, group) => sum + group.stats.estimated_minutes, 0),
    [plan.groups]
  );

  // Task 9 建的补录弹窗与条目列表挂在这里——是它们唯一的挂载点。
  const allTasks = useMemo(
    () => [...plan.groups.flatMap((group) => group.tasks), ...plan.backlog],
    [plan]
  );

  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [entryModalOpen, setEntryModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);

  const loadEntries = useCallback(async () => {
    try {
      const list = (await api.timeEntries.list()) as TimeEntry[];
      setEntries(list);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载计时记录失败');
    }
  }, []);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const openManualEntry = () => {
    setEditingEntry(null);
    setEntryModalOpen(true);
  };

  const openEditEntry = (entry: TimeEntry) => {
    setEditingEntry(entry);
    setEntryModalOpen(true);
  };

  // 补录/编辑/删除都会改变实花，任务卡的「实花/预估」和顶部「本周已记」都要
  // 跟着刷新——这两个数字是 tasks#plan 查出来的，reload() 已经在组件里，直接
  // 复用即可。
  const handleEntrySaved = () => {
    setEntryModalOpen(false);
    setEditingEntry(null);
    loadEntries();
    reload();
  };

  const handleEntryDeleted = () => {
    loadEntries();
    reload();
  };

  const dayGroups = plan.groups.slice(0, 2);

  return (
    <>
      <DndProvider backend={HTML5Backend}>
        <div className="mx-auto max-w-6xl px-4 py-6">
          <div className="mb-5">
            <h1 className="text-xl font-semibold text-gray-900">计划</h1>
            <p className="mt-1 text-sm text-gray-500">任务只存一份，五种切法查看同一批数据</p>
          </div>

          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <Segmented
              value={plan.group_by}
              options={SLICES}
              onChange={(value) => reload(value as PlanGroupBy)}
            />
            <div className="flex items-center gap-6 text-sm">
              <div>
                <div className="text-xs text-gray-500">本周已记</div>
                <div className="font-medium tabular-nums text-gray-900">
                  {formatDuration(Math.round(week_summary.total_seconds / 60))}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500">深度工作</div>
                <div className="font-medium tabular-nums text-gray-900">
                  {formatDuration(Math.round(week_summary.deep_seconds / 60))}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500">当前视图预估</div>
                <div className="font-medium tabular-nums text-gray-900">
                  {formatDuration(plannedMinutes)}
                </div>
              </div>
            </div>
          </div>

          <div className="mb-4 rounded-lg border border-gray-200 bg-white p-3">
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-sm font-medium text-gray-700">时间记录</h2>
              <Button size="small" onClick={openManualEntry}>
                手动补录
              </Button>
            </div>
            <TimeEntryList entries={entries} onEdit={openEditEntry} onDeleted={handleEntryDeleted} />
          </div>

          <Spin spinning={loading}>
            {plan.group_by === 'date' && (
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
                {dayGroups.map((group, index) => (
                  <DayColumn
                    key={group.key}
                    group={group}
                    date={group.key}
                    isToday={index === 0}
                    onDropTask={handleDropToDate}
                    onToggle={handleToggle}
                    onEdit={setEditing}
                  />
                ))}
                <BacklogPanel
                  tasks={plan.backlog}
                  onDropTask={handleDropToDate}
                  onToggle={handleToggle}
                  onEdit={setEditing}
                />
              </div>
            )}

            {plan.group_by === 'priority' && (
              <PriorityGrid
                groups={plan.groups}
                onDropTask={handleDropToPriority}
                onQuickSchedule={handleQuickSchedule}
                onToggle={handleToggle}
                onEdit={setEditing}
              />
            )}

            {['project', 'category', 'work_type'].includes(plan.group_by) && (
              <GroupedTaskList
                groups={plan.groups}
                onQuickSchedule={handleQuickSchedule}
                onToggle={handleToggle}
                onEdit={setEditing}
              />
            )}
          </Spin>

          <TaskEditModal
            open={editing !== null}
            task={editing}
            projects={projects}
            categories={categories}
            onCancel={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              reload();
            }}
          />

          <ManualEntryModal
            open={entryModalOpen}
            entry={editingEntry}
            projects={projects}
            tasks={allTasks}
            onCancel={() => setEntryModalOpen(false)}
            onSaved={handleEntrySaved}
          />
        </div>
      </DndProvider>
    </>
  );
};

export default Plan;
